// User role types - simplified to 3 roles
export type UserRole = 'admin' | 'operator' | 'viewer';
export type BackendUserRole = 'admin' | 'operator' | 'viewer';

// Backend User interface (matching alto-cero-smart-meter-platform)
export interface BackendUser {
  id: number;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  is_superuser?: boolean;
  is_active?: boolean;
  role?: string;
  primary_site?: string;
  primary_site_role?: string;
  primary_site_role_display?: string;
  site?: string;
  last_login?: string;
  date_joined?: string;
}

// Site interface for user's sites
export interface UserSite {
  site_id: string;
  site_name: string;
  role: string;
  is_primary: boolean;
}

// Complete user data from /me endpoint
export interface MeUser {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  primary_site: string;
  primary_site_role: string;
  sites: UserSite[];
}

// API Response for /me endpoint
export interface MeResponse {
  success: boolean;
  status: number;
  message: string;
  metadata: {
    timestamp: string;
    version: string;
  };
  data: {
    user: MeUser;
  };
}

// API Response wrappers
export interface UserResponse {
  success: boolean;
  message: string;
  data: BackendUser;
}

export interface UsersResponse {
  success: boolean;
  message: string;
  data: {
    users: BackendUser[];
    total?: number;
    page?: number;
    limit?: number;
  };
}

// API request interfaces
export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  site?: string;
}

export interface UpdateUserData {
  email?: string;
  first_name?: string;
  last_name?: string;
  password?: string;
  old_password?: string;
  is_active?: boolean;
  is_superuser?: boolean;
  role?: string;
}

export interface ChangePasswordData {
  old_password?: string;
  password: string;
}

// Role definitions for UI
export interface RoleDefinition {
  id: UserRole;
  name: string;
  description: string;
  color: string;
  permissions: string[];
}

export const roleDefinitions: RoleDefinition[] = [
  {
    id: 'admin',
    name: 'Administrator',
    description: 'Full system access with complete control',
    color: 'blue',
    permissions: [
      'Manage all users',
      'Configure system settings',
      'Access all data and reports',
      'Control all equipment',
      'Manage automations'
    ]
  },
  {
    id: 'operator',
    name: 'Operator',
    description: 'Operational control and monitoring',
    color: 'green',
    permissions: [
      'Control equipment',
      'View system data',
      'Manage automations',
      'Generate reports',
      'View user list'
    ]
  },
  {
    id: 'viewer',
    name: 'Viewer',
    description: 'Read-only access to system data',
    color: 'gray',
    permissions: [
      'View system data',
      'View reports',
      'View equipment status',
      'View automation status'
    ]
  }
];

// Helper functions
export const getRoleDefinition = (role: UserRole): RoleDefinition | undefined => {
  return roleDefinitions.find(r => r.id === role);
};

export const getRoleColor = (role: UserRole): string => {
  const definition = getRoleDefinition(role);
  return definition?.color || 'gray';
};

export const getRoleName = (role: UserRole): string => {
  const definition = getRoleDefinition(role);
  return definition?.name || 'Unknown';
};